import { Box, Grid, Stack, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import { getCampaignEmailLogs, IEmailCampaignLogDTO } from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  timeSince,
  WsColors,
  WsDataTable,
  WsDotColors,
  WsDotIcon,
  WsDrawer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import useFetchEmailLogs from '../hooks/useEmailLogs';

export type IconProps = {
  statusColor: string;
};
const EmailTextBorder = styled(Item)(({ theme }) => ({
  borderBottom: '1px solid ' + WsColors.TertiaryShades100,
  borderRadius: 0,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));
interface ContentData {
  from: string;
  subject: string;
  message: string;
  job: string;
}
const CampaignEmailLogs = () => {
  const [page, setPage] = useState<number>(0);
  const [campaignCode, SetCampaignCode] = useState<string>('');
  const [campaignLogs, setCampaignLogs] = useState<IEmailCampaignLogDTO[]>([]);
  const [content, setContent] = useState<ContentData>({
    from: '',
    subject: '',
    message: '',
    job: '',
  });
  useEffect(() => {
    const fetchCampaignEmailLogs = async () => {
      const getFetchCampaignEmailLogsReq = await getCampaignEmailLogs();
      const getFetchCampaignEmailLogsRes: IEmailCampaignLogDTO[] =
        getFetchCampaignEmailLogsReq.data;
      if (getFetchCampaignEmailLogsRes) {
        setCampaignLogs(getFetchCampaignEmailLogsRes);
      }
    };
    fetchCampaignEmailLogs();
  }, []);
  const result = useFetchEmailLogs(page, campaignCode);
  const defaultValues = {
    from: content?.from,
    subject: content?.subject,
    message: content?.message,
  };
  const formOptions = {
    defaultValues: defaultValues,
  };
  const methods = useForm(formOptions);
  const { setValue } = methods;
  useEffect(() => {
    setValue('from', content.from);
    setValue('subject', content.subject);
  }, [content, setValue]);
  const columns = useMemo(
    () => [
      {
        Header: 'Campaign Code',
        accessor: 'campaign_code',
      },
      {
        Header: 'Subject',
        accessor: 'subject',
      },
      {
        Header: 'Sent on',
        accessor: 'sent_on',
      },
      {
        Header: 'Campaign Size',
        accessor: 'campaign_size',
        Cell: (cell: Cell) => <WsDotIcon color={WsDotColors.GREEN} value={cell.value} />,
      },
    ],
    []
  );
  const sentColumns = useMemo(
    () => [
      {
        Header: 'To',
        accessor: 'to',
      },
    ],
    []
  );
  const cellCallbacks = [
    {
      id: 'campaign_code',
      callback: (name) => {
        SetCampaignCode(name);
        const payload = {
          from: sentEmailLogData[0]?.from ?? '',
          subject: sentEmailLogData[0]?.subject ?? '',
          message: sentEmailLogData[0]?.body ?? '',
          job: sentEmailLogData[0]?.job ?? '',
        };
        setContent(payload);
        setShowDrawer(true);
      },
    },
  ];
  const sentEmailLogData = useMemo(
    () =>
      result.list.map((list) => ({
        from: list.from,
        to: list.to,
        subject: list.subject,
        sent_on: String(timeSince(String(list.createdOn))),
        body: list.message,
        job: list.jobTitle,
      })),
    [result.list]
  );
  const emailLogData = useMemo(
    () =>
      campaignLogs.map((list) => ({
        campaign_code: list.campaignCode,
        subject: list.subject,
        sent_on: list.createdOn,
        campaign_size: list.campaignSize,
      })),
    [campaignLogs]
  );
  const [showDrawer, setShowDrawer] = useState(false);
  const FormLabel = (label: string) => (
    <Grid xs={2} item>
      <Stack direction={'row'} justifyContent={'space-between'} pr={2}>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          {label}
        </WsTypography>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          :
        </WsTypography>
      </Stack>
    </Grid>
  );
  const SubjectInput = () => (
    <FormProvider {...methods}>
      <Grid xs={4} item>
        <ControlledTextInput
          name="subject"
          type="text"
          fullWidth
          variant="standard"
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          InputProps={{
            disableUnderline: true,
            disabled: true,
          }}
        />
      </Grid>
    </FormProvider>
  );
  const JobField = () => (
    <FormProvider {...methods}>
      <Grid xs={4} item>
        <ControlledTextInput
          name="job"
          type="text"
          fullWidth
          variant="standard"
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          InputProps={{
            disableUnderline: true,
            disabled: true,
          }}
        />
      </Grid>
    </FormProvider>
  );
  const FromField = () => (
    <FormProvider {...methods}>
      <Grid xs={4} item>
        <ControlledTextInput
          name="from"
          id="title"
          type="text"
          fullWidth
          variant="standard"
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          InputProps={{
            disableUnderline: true,
            disabled: true,
          }}
        />
      </Grid>
    </FormProvider>
  );
  return (
    <Box>
      <WsDataTable
        columns={columns}
        data={emailLogData}
        totalDataItems={emailLogData.length}
        selectable={false}
        boldItems={['subject']}
        cellCallbacks={cellCallbacks}
      />
      <WsDrawer
        open={showDrawer}
        onClose={() => {
          setShowDrawer(false);
        }}
        title={'Job Description'}
      >
        <Box>
          <Grid>
            <Grid item>
              <EmailTextBorder>
                <Stack direction="row" alignItems={'center'}>
                  {FormLabel('From')}
                  {FromField()}
                  <Grid item xs={3}>
                    <Stack spacing={1} direction="row" justifyContent={'flex-end'}></Stack>
                  </Grid>
                </Stack>
              </EmailTextBorder>
              <EmailTextBorder>
                <Stack direction="row" alignItems={'center'}>
                  {FormLabel('Job')}
                  {JobField()}
                  <Grid item xs={3}>
                    <Stack spacing={1} direction="row" justifyContent={'flex-end'}></Stack>
                  </Grid>
                </Stack>
              </EmailTextBorder>
              <EmailTextBorder>
                <Stack direction="row" alignItems={'center'}>
                  {FormLabel('Subject')}
                  {SubjectInput()}
                  <Grid item xs={3}>
                    <Stack spacing={1} direction="row" justifyContent={'flex-end'}></Stack>
                  </Grid>
                </Stack>
              </EmailTextBorder>
            </Grid>
            <Grid marginLeft={2} item>
              <div dangerouslySetInnerHTML={{ __html: content.message }} />
            </Grid>
            <Grid item>
              <WsDataTable
                columns={sentColumns}
                data={sentEmailLogData}
                totalDataItems={sentEmailLogData.length}
                selectable={false}
                boldItems={['to']}
                cellCallbacks={cellCallbacks}
                pageable={true}
                fetchPage={(nextPage) => setPage(nextPage)}
              />
            </Grid>
          </Grid>
        </Box>
      </WsDrawer>
    </Box>
  );
};
export default CampaignEmailLogs;
